import React from 'react'
import { Card, Button, Form, Input, Checkbox, Alert, Typography, Spin, Result, Breadcrumb } from 'antd'
import { EditOutlined, CheckCircleOutlined, ArrowLeftOutlined, FilePdfOutlined, DownloadOutlined } from '@ant-design/icons'
import { getTBTPdfDownload } from '../../HttpRequests/tbtSignings'
import Screen from '../../Components/Screen'
import { getTeamUrl } from 'Common'
import PdfViewer from '../../Modals/TBTSigning/Components/PdfViewer'

const { Text } = Typography

export default class TBTSigningView extends React.PureComponent {

  constructor(props) {
    super(props)
    this.state = {
      signatureName: '',
      agreed: false,
      signatureData: null,
      signatureMode: 'draw',
      hasScrolledToBottom: false,
      downloading: false,
    }
    this.canvasRef = React.createRef()
    this.docContainerRef = React.createRef()
    this.isDrawing = false
    this.listenersAttached = false
  }

  componentDidMount() {
    this.props.componentDidMount(this.props.match.params.id)
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.documentUrl && this.props.documentUrl) {
      this.setupCanvas()
    }
    if (!prevProps.record && this.props.record && this.props.record.subcontractor) {
      this.setState({ signatureName: this.props.record.subcontractor.name || '' })
    }
  }

  componentWillUnmount() {
    if (this.props.documentUrl && this.props.documentType === 'pdf') {
      URL.revokeObjectURL(this.props.documentUrl)
    }
    window.removeEventListener('resize', this.resizeCanvas)
  }

  setupCanvas = () => {
    const canvas = this.canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    ctx.strokeStyle = '#000'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'

    const container = canvas.parentElement
    const dpr = window.devicePixelRatio || 1
    const cssHeight = 150
    const cssWidth = container ? container.clientWidth - 32 : 400
    canvas.width = Math.max(1, Math.floor(cssWidth * dpr))
    canvas.height = Math.floor(cssHeight * dpr)
    canvas.style.width = '100%'
    canvas.style.height = cssHeight + 'px'
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    if (!this.listenersAttached) {
      canvas.addEventListener('mousedown', this.startDrawing)
      canvas.addEventListener('mousemove', this.draw)
      canvas.addEventListener('mouseup', this.stopDrawing)
      canvas.addEventListener('mouseout', this.stopDrawing)
      canvas.addEventListener('touchstart', this.handleTouch, { passive: false })
      canvas.addEventListener('touchmove', this.handleTouch, { passive: false })
      canvas.addEventListener('touchend', this.stopDrawing)
      this.listenersAttached = true
    }

    window.addEventListener('resize', this.resizeCanvas, { passive: true })
  }

  resizeCanvas = () => {
    this.setupCanvas()
  }

  startDrawing = (e) => {
    this.isDrawing = true
    const rect = this.canvasRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const ctx = this.canvasRef.current.getContext('2d')
    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  draw = (e) => {
    if (!this.isDrawing) return
    const rect = this.canvasRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const ctx = this.canvasRef.current.getContext('2d')
    ctx.lineTo(x, y)
    ctx.stroke()
  }

  stopDrawing = () => {
    this.isDrawing = false
  }

  handleTouch = (e) => {
    e.preventDefault()
    const touch = e.touches[0]
    const mouseEvent = new MouseEvent(e.type === 'touchstart' ? 'mousedown' : 'mousemove', {
      clientX: touch.clientX,
      clientY: touch.clientY
    })
    this.canvasRef.current.dispatchEvent(mouseEvent)
  }

  clearSignature = () => {
    const canvas = this.canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }

  handleDocScroll = (e) => {
    const el = e.target
    const atBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 50
    if (atBottom && !this.state.hasScrolledToBottom) {
      this.setState({ hasScrolledToBottom: true })
    }
  }

  getSignatureImageData = () => {
    const canvas = this.canvasRef.current
    if (!canvas) return null
    const ctx = canvas.getContext('2d')
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const hasDrawing = imageData.data.some(pixel => pixel !== 0)
    if (!hasDrawing) return null
    return canvas.toDataURL('image/png')
  }

  handleDownloadSignedPdf = async () => {
    const { record } = this.props
    if (!record) return

    // Open window synchronously before any await - required for mobile browsers
    const newWindow = window.open('', '_blank')

    this.setState({ downloading: true })
    try {
      const response = await getTBTPdfDownload(record.id)
      if (response.data && response.data.download_url) {
        newWindow.location.href = response.data.download_url
      } else {
        newWindow.close()
      }
    } catch (error) {
      newWindow.close()
      const { message: antMessage } = require('antd')
      antMessage.error('Failed to download signed PDF')
    }
    this.setState({ downloading: false })
  }

  handleSubmit = () => {
    const { signatureName, agreed, signatureMode } = this.state

    if (!agreed) {
      alert('Please confirm that you have read and understood the Tool Box Talk document.')
      return
    }

    if (!signatureName.trim()) {
      alert('Please enter your name.')
      return
    }

    let signatureData = null
    if (signatureMode === 'draw') {
      signatureData = this.getSignatureImageData()
      if (!signatureData) {
        alert('Please draw your signature in the signature box.')
        return
      }
    }

    this.props.onSubmitSignature({
      signature_name: signatureName.trim(),
      signature_data: signatureData,
      agreed: true,
    })
  }

  render() {
    const { record, loading, documentUrl, documentType, documentLoading, signing, signed, error } = this.props
    const { signatureName, agreed, signatureMode, hasScrolledToBottom } = this.state

    // Debug info
    const debugInfo = [
      `Component: TBTSigningView`,
      `Loading: ${loading}`,
      `Document Loading: ${documentLoading}`,
      `Document URL: ${documentUrl ? 'Yes' : 'No'}`,
      `Document Type: ${documentType || 'N/A'}`,
      `Has Record: ${record ? 'Yes' : 'No'}`,
      `Record ID: ${record && record.id ? record.id : 'N/A'}`,
      `TBT Doc ID: ${record && record.toolbox_talk_document && record.toolbox_talk_document.id ? record.toolbox_talk_document.id : 'N/A'}`,
      `Is Signed: ${signed || (record && record.signed) ? 'Yes' : 'No'}`,
      `Error: ${error || 'None'}`,
      `Props Keys: ${Object.keys(this.props).join(', ')}`,
    ];

    if (loading) {
      return (
        <Screen>
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>Loading TBT document...</div>
          </div>
        </Screen>
      )
    }

    if (error) {
      return (
        <Screen>
          <Result
            status="error"
            title="Failed to load TBT document"
            subTitle={error}
            extra={[
              <Button key="back" onClick={() => window.history.back()}>
                Go Back
              </Button>
            ]}
          />
        </Screen>
      )
    }

    const isSigned = signed || (record && record.signed)
    const docTitle = record && record.toolbox_talk_document ? record.toolbox_talk_document.title : ''
    const subName = record && record.subcontractor ? record.subcontractor.name : ''
    const runDate = record && record.toolbox_talk_run && record.toolbox_talk_run.run_at
      ? new Date(record.toolbox_talk_run.run_at).toLocaleDateString()
      : 'N/A'

    return (
      <Screen>

        <Breadcrumb style={{ marginBottom: 16 }}>
          <Breadcrumb.Item>
            <a href={getTeamUrl('tbt-signing')}>TBT Signing</a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            {docTitle || 'View Document'}
          </Breadcrumb.Item>
        </Breadcrumb>

        <div style={{ marginBottom: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => window.history.back()}>
            Back to TBT List
          </Button>
        </div>

        {isSigned && (
          <Result
            status="success"
            icon={<CheckCircleOutlined />}
            title="Tool Box Talk Signed"
            subTitle={signed ? 'Thank you — your signature has been recorded.' : 'This Tool Box Talk has already been signed.'}
            extra={[
              <Button key="download" type="primary" icon={<DownloadOutlined />} loading={this.state.downloading} onClick={this.handleDownloadSignedPdf}>
                Download Signed PDF
              </Button>,
              <Button key="back" onClick={() => window.history.back()}>
                Back to TBT List
              </Button>
            ]}
            style={{ marginBottom: 24 }}
          />
        )}

        {!isSigned && (
          <Alert
            message="Please Review and Sign"
            description="Scroll through the entire document below, then complete your signature at the bottom."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {!isSigned && (
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>

          {/* Document Viewer */}
          <div style={{ flex: '1 1 600px', minWidth: 0 }}>
            <Card
              title={<span><FilePdfOutlined /> Tool Box Talk Document</span>}
              bodyStyle={{ padding: 0 }}
            >
              {documentLoading && (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 16 }}>Loading document...</div>
                </div>
              )}
              {!documentLoading && documentUrl && documentType === 'pdf' && (
                <div>
                  {window.innerWidth <= 768 && (
                    <Alert
                      message="Scroll within the PDF viewer below"
                      description="On mobile, please use touch gestures to scroll through the document inside the bordered area below. Do not scroll the page itself."
                      type="info"
                      showIcon
                      style={{ margin: 16, marginBottom: 12 }}
                    />
                  )}
                  <div
                    style={{
                      height: 'calc(100vh - 280px)',
                      minHeight: 500,
                      position: 'relative',
                      border: '3px solid #1890ff',
                      borderRadius: 4,
                      margin: 16,
                      overflow: 'hidden',
                    }}
                  >
                    <PdfViewer
                      url={documentUrl}
                      isMobile={true}
                      onLoadSuccess={(numPages) => console.log(`PDF loaded: ${numPages} pages`)}
                      onLoadError={(error) => console.log('PDF load error:', error)}
                      onScroll={this.handleDocScroll}
                    />
                  </div>
                </div>
              )}
              {!documentLoading && documentUrl && documentType === 'html' && (
                <div
                  ref={this.docContainerRef}
                  onScroll={this.handleDocScroll}
                  style={{
                    height: 'calc(100vh - 280px)',
                    minHeight: 500,
                    overflow: 'auto',
                  }}
                >
                  <iframe
                    src={documentUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      minHeight: 500,
                    }}
                    title="TBT Document"
                  />
                </div>
              )}
              {!documentLoading && !documentUrl && (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <Text type="secondary">No document available for this TBT record.</Text>
                </div>
              )}
            </Card>
          </div>

          {/* Signature Panel */}
          {!isSigned && (
            <div style={{ flex: '0 0 400px', minWidth: 320 }}>
              <Card title="Sign Document" size="small">

                <div style={{ marginBottom: 16 }}>
                  <Button.Group>
                    <Button
                      type={signatureMode === 'draw' ? 'primary' : 'default'}
                      onClick={() => this.setState({ signatureMode: 'draw' })}
                    >
                      <EditOutlined /> Draw Signature
                    </Button>
                    <Button
                      type={signatureMode === 'type' ? 'primary' : 'default'}
                      onClick={() => this.setState({ signatureMode: 'type' })}
                    >
                      Type Name
                    </Button>
                  </Button.Group>
                </div>

                <Form layout="vertical">
                  <Form.Item label="Full Name" required>
                    <Input
                      value={signatureName}
                      onChange={(e) => this.setState({ signatureName: e.target.value })}
                      placeholder="Enter your full name"
                      disabled={signing}
                      size="large"
                    />
                  </Form.Item>

                  {signatureMode === 'draw' && (
                    <Form.Item label="Draw Your Signature" required>
                      <div style={{
                        border: '2px dashed #d9d9d9',
                        borderRadius: 6,
                        padding: 16,
                        textAlign: 'center',
                        backgroundColor: '#fafafa',
                      }}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                          <EditOutlined /> Draw your signature in the box below
                        </Text>
                        <canvas
                          ref={this.canvasRef}
                          style={{
                            border: '1px solid #d9d9d9',
                            borderRadius: 4,
                            backgroundColor: 'white',
                            cursor: 'crosshair',
                            display: 'block',
                            width: '100%',
                          }}
                        />
                        <div style={{ marginTop: 8 }}>
                          <Button size="small" onClick={this.clearSignature} disabled={signing}>
                            Clear Signature
                          </Button>
                        </div>
                      </div>
                    </Form.Item>
                  )}

                  {signatureMode === 'type' && (
                    <Form.Item>
                      <div style={{
                        border: '2px dashed #d9d9d9',
                        borderRadius: 6,
                        padding: '24px 16px',
                        textAlign: 'center',
                        backgroundColor: '#fafafa',
                        minHeight: 100,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <span style={{
                          fontFamily: "'Brush Script MT', 'Segoe Script', cursive",
                          fontSize: 32,
                          color: '#333',
                        }}>
                          {signatureName || 'Your name will appear here'}
                        </span>
                      </div>
                    </Form.Item>
                  )}

                  <Form.Item>
                    <Checkbox
                      checked={agreed}
                      onChange={(e) => this.setState({ agreed: e.target.checked })}
                      disabled={signing}
                    >
                      I confirm that I have read, understood, and will comply with the requirements outlined in this Tool Box Talk document.
                    </Checkbox>
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      size="large"
                      onClick={this.handleSubmit}
                      loading={signing}
                      style={{ width: '100%' }}
                    >
                      {signing ? 'Submitting Signature...' : 'Submit Signature'}
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </div>
          )}

        </div>
        )}
      </Screen>
    )
  }
}
